import type { Entry, ReferencedEntry } from 'contentful';
import { useCallback, useState } from 'react';

import type { ModalAssembly } from 'api/contentful/contentTypes.cloverdotcom';
import { isEntry } from 'utils/EntryUtil';

/***
 * Internal Dynamic Modal Custom Hook used for powering the DynamicModals Context
 * Do not import directly.
 *
 * Maintains a collection of modals that can be rendered on top of a page
 ***/
export const useDynamicModalsInternal = (initialValue = new Set<Entry<ModalAssembly>>()) => {
  const [modals, setModals] = useState<Set<Entry<ModalAssembly>>>(initialValue);

  const add = useCallback((modal?: ReferencedEntry<ModalAssembly>) => {
    if (!isEntry(modal)) return;

    setModals(prevModals => {
      if (prevModals.has(modal)) return prevModals;

      const newModals = new Set(prevModals);
      newModals.add(modal);
      return newModals;
    });
  }, []);

  const clear = useCallback(() => setModals(new Set()), []);

  return { modals: Array.from(modals.values()), add, clear };
};

export type DynamicModalContext = ReturnType<typeof useDynamicModalsInternal>;
